<template>
  <!-- prettier-ignore -->
  <div class="Expander">
    <div
      class="trigger"
      :class="open ? 'active' : 'beforeBorder'"
      @click="toggle">
      <span
        class="title"
        v-html="title" />
      <div class="trigger-icon-wrapper">
        <svg
          class="trigger-icon"
          :class="{ open: open }"
          viewBox="0 0 40 12">
          <polyline
            points="12,2 20,10 28,2"
            stroke-width="3"
            fill="none" />
        </svg>
      </div>
    </div>
    <transition :name="animation">
      <div
        v-show="open"
        class="body">
        <slot />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'

// Props
const props = defineProps({
  title: {
    type: String,
    default: 'title',
  },

  animation: {
    type: String,
    default: 'topToBottom',
    // validator: prop => ['leftToRight', 'bounceIn', 'bottomToTop'].includes(prop)
  },

  initializeOpened: {
    type: Boolean,
    required: true,
  },
})

// Data
const open = ref(false)

// Init
const $emit = defineEmits(['opened'])
init()

// Methods
function init() {
  if (props.initializeOpened) {
    // do not emit event, as it's automatically opened
    open.value = true
  }
}

function toggle() {
  open.value = !open.value
  // emit opened event for tracking
  if (open.value) {
    $emit('opened')
  }
}
</script>

<style>
.beforeBorder {
  position: relative;

  &:before {
    transition: opacity 0.1s linear, transform 0.5s ease-in-out;
    position: absolute;
    border-bottom: 1px solid var(--c-original-green);
    content: '';
    width: 100%;
    left: 0;
    bottom: -1px;
  }

  &:not(:hover)::before {
    transform: scaleX(0);
    opacity: 0;
  }
}

.Expander {
  overflow: hidden;

  .trigger {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0.7rem 0.5rem;
    border-bottom: 1px solid var(--c-gray);
    position: relative;
    z-index: 1;
    background-color: white;

    &:hover {
      color: var(--c-original-green);
    }

    &.active {
      border-bottom-color: var(--c-original-green);
    }

    .title {
      flex: 1;
    }

    .trigger-icon-wrapper {
      width: 40px;
      height: 12px;

      .trigger-icon {
        stroke: var(--c-original-green);
        transition: transform 0.2s cubic-bezier(0.23, 1, 0.32, 1);

        &.open {
          stroke: var(--c-gray);
          transform: rotate(180deg);
        }
      }
    }
  }

  .body {
    padding: 0 10px;
  }
}

@media screen and (max-width: 299px) {
  .Expander {
    .trigger {
      .title {
        font-size: var(--pw-font-size-small);
      }

      .trigger-icon-wrapper {
        width: 30px;
      }
    }
  }
}

.rightToLeft-enter-active {
  animation: rightToLeft 0.5s;
}

.rightToLeft-leave-active {
  animation: rightToLeft 0.5s reverse;
}

@keyframes rightToLeft {
  0% {
    transform: translateX(100vw);
  }
  50% {
    transform: translateX(-2em);
  }
  100% {
    transform: translateX(0);
  }
}

.leftToRight-enter-active {
  animation: leftToRight 0.5s;
}

.leftToRight-leave-active {
  animation: leftToRight 0.5s reverse;
}

@keyframes leftToRight {
  0% {
    transform: translateX(-100vw);
  }
  50% {
    transform: translateX(2em);
  }
  100% {
    transform: translateX(0);
  }
}

.topToBottom-enter-active {
  animation: topToBottom 0.5s forwards;
}

.topToBottom-leave-active {
  animation: topToBottom 0.5s reverse;
}

@keyframes topToBottom {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
